import { useEffect, useRef } from 'react';

import type { InfiniteScrollRef } from '@/components/composite/InfiniteScroll/InfiniteScroll';

type UseTypewriterAutoScrollParams = {
    scrollRef: React.RefObject<InfiniteScrollRef | null>;
    typewriterMessageId?: string | null;
    onTypewriterComplete?: (messageId: string) => void;
};

const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);

export function useTypewriterAutoScroll({
    scrollRef,
    typewriterMessageId = null,
    onTypewriterComplete
}: UseTypewriterAutoScrollParams) {
    const previousTypewriterMessageIdRef = useRef<string | null>(null);
    const autoFollowTypingRef = useRef(false);

    useEffect(() => {
        if (!typewriterMessageId) {
            autoFollowTypingRef.current = false;
            previousTypewriterMessageIdRef.current = null;
            return;
        }

        if (previousTypewriterMessageIdRef.current !== typewriterMessageId) {
            autoFollowTypingRef.current = true;
            previousTypewriterMessageIdRef.current = typewriterMessageId;
        }

        const scrollElement = scrollRef.current?.getScrollElement();

        if (!scrollElement) return;

        const disableAutoFollow = () => {
            autoFollowTypingRef.current = false;
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (SCROLL_KEYS.has(event.key)) {
                disableAutoFollow();
            }
        };

        scrollElement.addEventListener('wheel', disableAutoFollow, { passive: true });
        scrollElement.addEventListener('touchstart', disableAutoFollow, { passive: true });
        scrollElement.addEventListener('pointerdown', disableAutoFollow, { passive: true });
        scrollElement.addEventListener('keydown', handleKeyDown);

        let frameId = 0;
        const keepBottomPinned = () => {
            if (!autoFollowTypingRef.current) return;

            scrollRef.current?.scrollToBottom();
            frameId = requestAnimationFrame(keepBottomPinned);
        };

        frameId = requestAnimationFrame(keepBottomPinned);

        return () => {
            cancelAnimationFrame(frameId);
            scrollElement.removeEventListener('wheel', disableAutoFollow);
            scrollElement.removeEventListener('touchstart', disableAutoFollow);
            scrollElement.removeEventListener('pointerdown', disableAutoFollow);
            scrollElement.removeEventListener('keydown', handleKeyDown);
        };
    }, [scrollRef, typewriterMessageId]);

    const handleTypewriterComplete = (messageId: string) => {
        if (messageId === typewriterMessageId) {
            autoFollowTypingRef.current = false;
            previousTypewriterMessageIdRef.current = null;
        }

        onTypewriterComplete?.(messageId);
    };

    return {
        handleTypewriterComplete
    };
}
